import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'

/**
 * Eine fremde Adresse öffnen — im Browser und in der nativen Hülle.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM ES DIESE FUNKTION GIBT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Im Browser ist `window.location.href = url` richtig. In einer WebView ist es
 * ein Fehler, und zwar ein teurer: die WebView trägt sich selbst zur fremden
 * Seite, die Anwendung ist damit weg — keine Adressleiste, kein Zurück-Knopf,
 * auf iOS eine Sackgasse. Der Vermieter muss die App abwürgen und weiß nicht,
 * ob seine Zahlung durchging.
 *
 * `window.open` ist der schlimmere der beiden Fälle: es wird in einer WebView
 * in der Regel blockiert, ohne Fehler und ohne Meldung. Es sieht aus wie ein
 * Knopf, den man nicht gedrückt hat.
 *
 * Beides betraf am 31.07.2026 ausschließlich Wege, an denen Geld hängt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM EINE FUNKTION UND NICHT SIEBEN REPARATUREN
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Sieben Stellen einzeln zu ändern behebt sieben Fehler. Diese Funktion behebt
 * auch den achten — den, den jemand in drei Monaten einbaut, ohne von dem
 * Problem zu wissen. Der Unterschied zwischen Browser und Hülle gehört an EINE
 * Stelle; überall sonst steht nur noch „öffne diese Adresse".
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM SIE WARTET
 * ════════════════════════════════════════════════════════════════════════════
 *
 * In der Hülle löst das Versprechen erst auf, wenn der Benutzer die Seite
 * schließt. Damit steht das Nachladen sichtbar hinter dem Öffnen:
 *
 *     await oeffneExtern(pay_url)
 *     await zahlungsstandNeuLaden()
 *
 * Bei einem Rückruf läge die zweite Zeile weiter unten, und genau dort wird sie
 * vergessen. Die Folge wäre: die Zahlung ist bei Stripe angekommen, die
 * Anwendung zeigt sie nicht — für den Vermieter sieht das aus wie verlorenes
 * Geld.
 *
 * Im Browser löst das Versprechen NIE auf, weil die Seite verlassen wird. Das
 * ist richtig so: dort gibt es keine Rückkehr, sondern einen Neuaufbau. Wer
 * hinter dem `await` etwas stehen hat, verliert dadurch nichts — es wäre beim
 * Verlassen der Seite ohnehin nicht mehr gelaufen.
 */
export async function oeffneExtern(url: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    window.location.href = url
    // Die Seite wird verlassen; alles dahinter läuft nicht mehr. Das
    // Versprechen absichtlich offen lassen, damit sich der Aufrufer in beiden
    // Fällen gleich verhält.
    return new Promise<void>(() => {})
  }

  /*
   * Auf das Schließen warten.
   *
   * `browserFinished` meldet, dass der Benutzer den eingebetteten Browser
   * verlassen hat.
   *
   * DER ZUHÖRER WIRD VOR DEM ÖFFNEN ANGEMELDET. Andersherum gäbe es ein
   * Zeitfenster, in dem der Benutzer schon geschlossen hat, bevor jemand
   * hinhört — dann wartet das Versprechen für immer und die Ansicht bleibt
   * hängen.
   *
   * Und er wird danach wieder abgemeldet: bliebe er stehen, liefe beim nächsten
   * Öffnen zusätzlich das Nachladen der vorigen Ansicht.
   */
  const geschlossen = new Promise<void>((auf) => {
    const zuhoerer = Browser.addListener('browserFinished', () => {
      void zuhoerer.then((z) => z.remove())
      auf()
    })
  })

  await Browser.open({ url })
  await geschlossen
}
